/**
 * dateUtils.js — Shared date/time formatting utilities for Green Door.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AGENT INSTRUCTIONS — READ BEFORE WRITING ANY DATE/TIME CODE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The DB stores dates in up to 5 formats (all are valid — do NOT try to change
 * existing stored values):
 *   1. "2023-03-15T00:00:00+00:00"   — Python datetime ISO (UTC midnight)
 *   2. "2026-07-17T12:00:00.000Z"    — JS noon-UTC (legacy CoverRecordDialog)
 *   3. "2026-06-10"                  — Plain date string (FarmFolio, breeding)
 *   4. "2026-02-08T22:54:16.472Z"    — Full UTC timestamp (created_at etc.)
 *   5. "2025-05-01"                  — Plain date string (sold_date etc.)
 *
 * RULES:
 *   1. NEVER use `new Date("YYYY-MM-DD")` for display — it parses as UTC
 *      midnight and shows one day early in negative-UTC timezones.
 *   2. Use fmtDate()     for date-only fields (birthdate, sale_date, cover_date,
 *      invoice_date, withdrawal_date, ship_in_date, etc.)
 *   3. Use fmtDatetime() for timestamp fields (created_at, updated_at,
 *      log_time, sent_at, cached_at, etc.) — pass userTimezone from AuthContext.
 *   4. The user's timezone comes from: const { userTimezone } = useAuth()
 *   5. extractDateStr()  returns "YYYY-MM-DD" for form inputs and comparisons.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

const LOCALE = 'en-US';

/**
 * Parse any stored date value into a plain local Date (no UTC shift).
 * Extracts YYYY-MM-DD portion and constructs with local date constructor.
 * @private
 */
function _parseDateOnly(d) {
  if (!d) return null;
  const s = String(d).trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
}

/**
 * Format any date value as "Jun 15, 2025".
 * Safe for ALL date-only fields — does not shift due to UTC offset.
 * @param {string|null|undefined} d
 * @returns {string}
 */
export function fmtDate(d) {
  const dt = _parseDateOnly(d);
  if (!dt || isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format any date value as "Jun 15" (no year). For compact spaces.
 * @param {string|null|undefined} d
 * @returns {string}
 */
export function fmtDateShort(d) {
  const dt = _parseDateOnly(d);
  if (!dt || isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
}

/**
 * Format a UTC datetime string as "Jun 15, 2025, 2:30 PM" in the user's timezone.
 * Use for ALL timestamp fields: created_at, updated_at, sent_at, cached_at, log_time.
 * @param {string|null|undefined} d  — UTC ISO string
 * @param {string} [tz]              — IANA timezone from AuthContext userTimezone
 * @returns {string}
 */
export function fmtDatetime(d, tz) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '—';
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(dt);
  } catch {
    return '—';
  }
}

/**
 * Format a UTC datetime as a time only: "2:30 PM".
 * @param {string|null|undefined} d
 * @param {string} [tz]
 * @returns {string}
 */
export function fmtTime(d, tz) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '—';
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: tz || Intl.DateTimeFormat().resolvedOptions().timeZone,
      hour: 'numeric', minute: '2-digit',
    }).format(dt);
  } catch {
    return '—';
  }
}

/**
 * Convert a UTC ISO datetime string into the "YYYY-MM-DDTHH:MM" format that
 * a <input type="datetime-local"> expects, expressed in the given IANA timezone.
 *
 * Use this when pre-filling a datetime-local input from a stored UTC value so
 * the user sees the correct local time per their profile timezone, not the
 * browser's system timezone.
 *
 * @param {string} utcStr  — UTC ISO string, e.g. "2026-08-29T05:04:00+00:00"
 * @param {string} tz      — IANA timezone from AuthContext userTimezone
 * @returns {string}       — "YYYY-MM-DDTHH:MM" in tz, or "" on failure
 */
export function utcToInputTz(utcStr, tz) {
  if (!utcStr || !tz) return '';
  try {
    const dt = new Date(utcStr);
    if (isNaN(dt.getTime())) return '';
    return new Intl.DateTimeFormat('sv', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(dt).replace(' ', 'T').slice(0, 16);
  } catch {
    return '';
  }
}

/**
 * Convert a datetime-local input value ("YYYY-MM-DDTHH:MM") — which represents
 * a moment in the given IANA timezone — back to a UTC ISO string for storage.
 *
 * Use this when saving from a datetime-local input that was pre-filled via
 * utcToInputTz(), so the value is interpreted in the correct profile timezone
 * rather than the browser's system timezone.
 *
 * @param {string} localStr  — "YYYY-MM-DDTHH:MM" as entered in the input
 * @param {string} tz        — IANA timezone the value is expressed in
 * @returns {string}         — UTC ISO string, or "" on failure
 */
export function inputTzToUTC(localStr, tz) {
  if (!localStr || !tz) return '';
  try {
    // Treat the local string as if it were UTC to get a reference epoch
    const guessUTC = new Date(localStr + ':00.000Z');
    if (isNaN(guessUTC.getTime())) return '';
    // Find what guessUTC looks like in the target timezone
    const inTzStr = new Intl.DateTimeFormat('sv', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(guessUTC).replace(' ', 'T');
    // The difference gives the timezone offset at this moment
    const offsetMs = guessUTC.getTime() - new Date(inTzStr + '.000Z').getTime();
    return new Date(guessUTC.getTime() + offsetMs).toISOString();
  } catch {
    return '';
  }
}


export function extractDateStr(d) {
  if (!d) return '';
  const m = String(d).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}
